import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AuroraBackground } from './ui/aurora-background';
import { SpotlightEffect } from './ui/spotlight-effect';
import { ParticleField } from './ui/particle-field';
import { GridOverlay } from './ui/grid-overlay';

export const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Typing effect state
  const phrases = ['Actually Work', 'Scale', 'Convert', 'Grow'];
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Mouse parallax effect (desktop only)
  useEffect(() => {
    if (window.innerWidth <= 768) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 2;
        const y = (e.clientY / window.innerHeight - 0.5) * 2;
        setMousePosition({ x, y });
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll-triggered fade-out
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const progress = Math.min(scrollY / (windowHeight * 0.5), 1);
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typing effect
  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    const typingSpeed = isDeleting ? 30 : 50;
    const pauseTime = isDeleting ? 0 : 2000;

    if (!isDeleting && currentText === currentPhrase) {
      // Pause before deleting
      const timeout = setTimeout(() => setIsDeleting(true), pauseTime);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && currentText === '') {
      // Move to next phrase
      setIsDeleting(false);
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    // Type or delete character
    const timeout = setTimeout(() => {
      setCurrentText((prev) => {
        if (isDeleting) {
          return currentPhrase.substring(0, prev.length - 1);
        }
        return currentPhrase.substring(0, prev.length + 1);
      });
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentPhraseIndex, phrases]);

  // Cursor blink
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-[#0a0a0a] overflow-hidden">
      {/* Layer 1: Aurora Gradient Mesh */}
      <AuroraBackground parallaxOffset={mousePosition} />
      
      {/* Layer 2: Spotlight Glow */}
      <SpotlightEffect parallaxOffset={mousePosition} />
      
      {/* Layer 2.5: Particle Field */}
      <ParticleField />
      
      {/* Layer 3: Grid Overlay */}
      <GridOverlay parallaxOffset={mousePosition} />

      {/* Layer 5: Content */}
      <div 
        className="container-professional text-center py-20 relative z-10"
        style={{
          opacity: 1 - scrollProgress * 0.8,
          transform: `translateY(${scrollProgress * 50}px)`,
          filter: `blur(${scrollProgress * 3}px)`,
          transition: 'filter 0.3s ease-out',
        }}
      >
        {/* Animated Headline with Typing Effect */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
          <span
            className="inline-block opacity-0"
            style={{
              animation: isLoaded ? 'fade-up 0.6s ease-out forwards' : 'none',
              animationDelay: '0s',
            }}
          >
            Build Products That{'\u00A0'}
          </span>
          <span
            className="inline-block text-primary opacity-0"
            style={{
              animation: isLoaded ? 'fade-up 0.6s ease-out forwards' : 'none',
              animationDelay: '0.2s',
            }}
          >
            {currentText}
            <span 
              className="inline-block w-[2px] h-[0.9em] bg-primary ml-1 align-middle"
              style={{ opacity: showCursor ? 1 : 0 }}
            />
          </span>
        </h1>
        
        {/* Subheadline with simple fade */}
        <p 
          className="text-lg md:text-xl text-[rgba(255,255,255,0.65)] max-w-3xl mx-auto mb-10 leading-relaxed opacity-0"
          style={{
            animation: isLoaded ? 'fade-in 0.8s ease-out forwards' : 'none',
            animationDelay: '0.8s',
          }}
        >
          Custom web apps, mobile apps, and websites delivered with clear communication, honest timelines, and measurable results.
        </p>
        
        {/* Subtle CTA Button */}
        <div 
          className="flex justify-center mb-12 opacity-0"
          style={{
            animation: isLoaded ? 'scale-in 0.6s ease-out forwards' : 'none',
            animationDelay: '1.1s',
          }}
        >
          <Link to="/contact">
            <button className="group relative h-12 px-8 text-sm bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all duration-300 hover:transform hover:-translate-y-0.5 flex items-center gap-2 overflow-hidden shadow-lg hover:shadow-xl">
              <span className="relative z-10">Start Your Project</span>
              <ArrowRight className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Link>
        </div>
        
        {/* Service Tags */}
        <div 
          className="flex justify-center opacity-0"
          style={{
            animation: isLoaded ? 'fade-in 0.8s ease-out forwards' : 'none',
            animationDelay: '1.4s',
          }}
        >
          <span className="text-xs text-[rgba(255,255,255,0.4)] uppercase tracking-wider font-mono">
            Web apps • Mobile apps • Websites • AI‑powered flows
          </span>
        </div>
      </div>
    </section>
  );
};
